import { z } from 'zod';
import { ArtifactKind, ErrorCode, IsoDateTime, JobStatus, JobType, PackageId, Uuid, } from './primitives.js';
/**
 * No `framework` option exists: Vite React is the only emitter until the
 * expansion lanes are promoted (platform-targets.md).
 */
export const JobOptions = z.object({
    /** Mode D: page paths to rebuild. Defaults to Home. */
    pages: z.array(z.string()).max(15).optional(),
    /** Mode D: re-run of an existing generated page — spends a retry, not an AI page. */
    retryOfJobId: Uuid.optional(),
    includeScreenshots: z.boolean().default(false),
});
export const CreateJobRequest = z.object({
    type: JobType,
    options: JobOptions.default({}),
});
export const EntitlementSnapshot = z.object({
    packageId: PackageId,
    aiPagesRemainingAfter: z.number().int().min(0),
    retriesRemainingAfter: z.number().int().min(0),
});
export const CreateJobResponse = z.object({
    jobId: Uuid,
    status: JobStatus,
    /** null for free job types */
    entitlement: EntitlementSnapshot.nullable(),
});
export const JobStepId = z.enum(['extract', 'ir', 'codegen', 'verify', 'package', 'upload']);
export const JobStep = z.object({
    id: JobStepId,
    state: z.enum(['pending', 'running', 'done', 'failed', 'skipped']),
    startedAt: IsoDateTime.optional(),
    finishedAt: IsoDateTime.optional(),
    /** Live caption while a step is in flight, e.g. "section 3 of 6 — BlogList". */
    detail: z.string().max(240).optional(),
});
export const JobProgress = z.object({
    steps: z.array(JobStep),
    percent: z.number().min(0).max(100),
});
export const JobArtifactView = z.object({
    id: Uuid,
    kind: ArtifactKind,
    bytes: z.number().int().nonnegative().nullable(),
    expiresAt: IsoDateTime.nullable(),
});
export const JobView = z.object({
    id: Uuid,
    projectId: Uuid,
    type: JobType,
    status: JobStatus,
    progress: JobProgress,
    error: z.object({ code: ErrorCode, message: z.string() }).nullable(),
    artifacts: z.array(JobArtifactView),
    /**
     * The scan this job read (or, for a scan job, the one it produced). The hub compares it with
     * the current snapshot to say when a download was built before the last re-scan.
     */
    scanSnapshotId: Uuid.nullable(),
    createdAt: IsoDateTime,
    finishedAt: IsoDateTime.nullable(),
});
/** Recent jobs of one project, newest first — the hub's history and artifact table. */
export const JobListResponse = z.object({
    items: z.array(JobView),
});
/** What `POST /jobs/:id/cancel` answers once the job is no longer queued or running. */
export const CancelJobResponse = z.object({
    id: Uuid,
    status: JobStatus,
});
/**
 * `DETACHLY_REPORT.md`, as text rather than as a download. A rebuild's honest account of what it
 * could and could not do is the last thing that should require unzipping something to read (F18).
 */
export const JobReportResponse = z.object({
    jobId: Uuid,
    projectId: Uuid,
    markdown: z.string(),
    artifactId: Uuid,
    bytes: z.number().int().nonnegative(),
    /** When the job that wrote it finished, so the page can date the report it shows. */
    finishedAt: IsoDateTime.nullable(),
});
/**
 * A short-lived URL the browser can follow straight to storage. Returned as JSON rather than a
 * 302 so the dashboard can attach it to a link and show the expiry (api-spec.md).
 */
export const ArtifactDownloadResponse = z.object({
    url: z.string().url(),
    expiresAt: IsoDateTime,
    kind: ArtifactKind,
    filename: z.string().min(1),
    bytes: z.number().int().nonnegative().nullable(),
});
//# sourceMappingURL=jobs.js.map